import { createContext, useContext, useState, useEffect } from 'react'
import { getProfile } from '../api/services'

const AuthContext = createContext(null)

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [loading, setLoading] = useState(true)

    // High-End Onboarding Context State Tracker
    const [isNewUserRegistration, setIsNewUserRegistration] = useState(false)

    useEffect(() => {
        const token = localStorage.getItem('gmc_token')
        if (token) {
            getProfile()
                .then((res) => setUser(res.data))
                .catch((err) => {
                    // Only clear the session if the token is genuinely rejected
                    // (401). A transient network/CORS/5xx error must NOT wipe a
                    // valid token — that would log the user out on a blip. In
                    // that case keep the token; the profile can load on a later
                    // call. (The client interceptor already handles the 401 →
                    // /login redirect for /auth/me, so this is belt-and-braces.)
                    if (err?.response?.status === 401) {
                        localStorage.removeItem('gmc_token')
                        setUser(null)
                    }
                })
                .finally(() => setLoading(false))
        } else {
            setLoading(false)
        }
    }, [])

    /**
     * Session Initialization
     * @param {string} token - JWT Access Signature
     * @param {Object} userData - Hydrated User Profile Payload
     * @param {Object} options - Custom configuration constraints
     * @param {boolean} options.isNewUser - Flags the entry lane as a fresh registration
     */
    const loginWithToken = (token, userData, options = { isNewUser: false }) => {
        localStorage.setItem('gmc_token', token)
        setIsNewUserRegistration(!!options.isNewUser)
        setUser(userData)
    }

    /**
     * Re-fetch the profile from the server and replace the cached user.
     *
     * Needed after any action that changes server-side state the UI depends
     * on — accepting the onboarding agreement, for example. Without it the
     * cached user would still show terms_accepted false and the modal would
     * not close.
     *
     * Returns the fresh profile so callers can act on it directly.
     */
    const refreshUser = async () => {
        try {
            const res = await getProfile()
            setUser(res.data)
            return res.data
        } catch (err) {
            // A transient failure must not log the user out. The 401 case is
            // already handled by the client interceptor.
            console.error('Could not refresh profile:', err)
            return null
        }
    }

    const logout = () => {
        // Clear auth token
        localStorage.removeItem('gmc_token')
        setIsNewUserRegistration(false)
        setUser(null)
    }

    return (
        <AuthContext.Provider
            value={{
                user,
                loading,
                isNewUserRegistration,
                setRegistrationOnboarding: setIsNewUserRegistration,
                loginWithToken,
                refreshUser,
                logout
            }}
        >
            {children}
        </AuthContext.Provider>
    )
}

export const useAuth = () => useContext(AuthContext)